import type { MouseEvent } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { BackButton, EventCard } from '@/components';
import { EventsListLayout } from '@/features/EventsListLayout';
import { EventsBrowseBar, EventFilter, EventSort } from '@/features/BrowseBar';
import type { EventsBrowseBarConfig } from '@/features/BrowseBar';
import { EventFilterView } from '@/features/EventFilterView';
import { SidePanel } from '@/components/SidePanel';
import { usePaginatedDatabaseEvents } from '@/features/EventsListLayout/hooks';
import { EngineService } from '@/services/engine';
import { useSourcesStore } from '@/stores';
import { useDocumentTitle, useOpenInNewWindow } from '@/hooks';
import { useSortFromUrl } from '@/hooks/useSortFromUrl';
import { SortOption } from '@/utils/query-filters';
import { hasRouterHistory } from '@/stores/navigation-history.store';
import { buildEventCardMetadata } from '@/utils/event-card-metadata';
import {
    buildDatabaseEventWatchUrl,
    getDatabaseEventLicensePlate,
    getDatabaseEventName,
} from '@/utils/database-event-label';
import type { DatabaseEvent } from '@/types/event';

const DATABASE_EVENTS_CONFIG: EventsBrowseBarConfig = {
    filters: [EventFilter.DATE_RANGE, EventFilter.SOURCE],
    sorts: [EventSort.DATE],
};

export default function DatabaseEventsPage() {
    useDocumentTitle('databaseEvents');
    const { t } = useTranslation([I18nNamespace.COMMON, I18nNamespace.EVENTS]);
    const [sortOption] = useSortFromUrl(SortOption.DATE_NEWEST);
    const { openUrl } = useOpenInNewWindow();
    const getSourceById = useSourcesStore((state) => state.getSourceById);

    const {
        events,
        totalItems,
        page,
        pageSize,
        onPageChange,
        error,
        isLoading,
        isFetching,
        pageStateKey,
    } = usePaginatedDatabaseEvents({ pageKey: 'database-events', sortOption });

    const openDatabaseItem = useCallback(
        (databaseEvent: DatabaseEvent, clickEvent?: MouseEvent) => {
            openUrl(buildDatabaseEventWatchUrl(databaseEvent), clickEvent);
        },
        [openUrl],
    );

    if (error) {
        return (
            <div className="h-full flex items-center justify-center">
                <p className="text-red-400">{t('events:page.errorLoading')}</p>
            </div>
        );
    }

    return (
        <div className="relative h-full min-h-0">
            <SidePanel side="left">
                <EventFilterView config={DATABASE_EVENTS_CONFIG} />
            </SidePanel>
            {hasRouterHistory() && <BackButton variant="floating" />}
            <EventsListLayout
                pageKey={pageStateKey ?? 'database-events'}
                eventCount={totalItems || events.length}
                isEmpty={events.length === 0}
                isLoading={isLoading}
                pagination={{
                    mode: 'paginated',
                    page,
                    pageSize,
                    totalCount: totalItems,
                    isFetching,
                    onPageChange,
                }}
                actionBar={
                    <EventsBrowseBar
                        config={DATABASE_EVENTS_CONFIG}
                        initialSortOption={SortOption.DATE_NEWEST}
                    />
                }
                emptyMessage={t('events:page.noDatabaseEvents')}
            >
                {events.map((databaseEvent) => {
                    const event = databaseEvent.event;
                    const source = getSourceById(event.sourceId);
                    const sourceType = event.sourceType ?? source?.type;
                    const sourceLabel =
                        databaseEvent.sourceName ??
                        event.sourceName ??
                        source?.displayName ??
                        '';
                    const metadata = buildEventCardMetadata({
                        createdAt: databaseEvent.createdAt,
                        isVideoRange: false,
                        sourceType,
                        sourceLabel,
                        locationName: source?.location?.name,
                    });
                    return (
                        <EventCard
                            key={databaseEvent.id}
                            id={event.id}
                            title={getDatabaseEventName(t, databaseEvent)}
                            licensePlate={getDatabaseEventLicensePlate(
                                databaseEvent,
                            )}
                            thumbnailUrls={[
                                event.thumbnailUrl ||
                                    EngineService.getEventThumbnailUrl(
                                        event.id,
                                        true,
                                    ),
                            ]}
                            confidence={
                                databaseEvent.confidence != null
                                    ? databaseEvent.confidence * 100
                                    : undefined
                            }
                            metadata={metadata}
                            onClick={(_, clickEvent) =>
                                openDatabaseItem(databaseEvent, clickEvent)
                            }
                        />
                    );
                })}
            </EventsListLayout>
        </div>
    );
}
